import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { tagAnatomySizes } from "./componentAnatomyTagSizes";

// Mined from designSystem/Tag/Tag.module.scss against the spec recipe and
// geometry tables. Hover applies only to .interactive; hoverX paints the X
// button through --tag-x-hover-bg while the body reverts to resting.

const parts: readonly AnatomyPart[] = [
    {
        id: "background",
        label: "Root",
        element: "background-color on .tag, via --tag-bg / --tag-bg-hover",
    },
    {
        id: "border",
        label: "Border",
        element: "1px border on .tag, via --tag-border / --tag-border-hover",
    },
    {
        id: "label",
        label: "Label",
        element: "color and text style on .label, via --tag-fg",
    },
    {
        id: "glyph",
        label: "Left icon slot",
        element: ".slot wrapping the leading icon, inheriting --tag-fg",
    },
    {
        id: "xButton",
        label: "X button",
        element: "20×20 .xButton, hover fill via --tag-x-hover-bg",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector: ".interactive:hover:not(:has(.xButton:hover))",
    },
];

const fill = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "background-color",
    token,
    via: "--tag-bg",
});

const fillHover = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "background-color",
    token,
    via: "--tag-bg-hover",
});

const edge = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--tag-border",
});

const edgeHover = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--tag-border-hover",
});

const labelColor = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
    via: "--tag-fg",
});

const xHover = (token: `--${string}`): AnatomyBinding => ({
    partId: "xButton",
    property: "--tag-x-hover-bg",
    token,
    via: "--tag-x-hover-bg",
    note: "Applied as the X background while it is hovered (hoverX); the tag body reverts to its resting recipe.",
});

const transparentFill: AnatomyBinding = {
    partId: "background",
    property: "background-color",
    literal: "transparent",
    via: "--tag-bg",
};

const transparentEdge: AnatomyBinding = {
    partId: "border",
    property: "border-color",
    literal: "transparent",
    via: "--tag-border",
    note: "Solid tags keep a 1px transparent border so outline never shifts layout.",
};

const solidVariant = ({
    id,
    bg,
    fg,
    hoverBg,
    xHoverBg,
}: {
    id: string;
    bg: `--${string}`;
    fg: `--${string}`;
    hoverBg: `--${string}`;
    xHoverBg: `--${string}`;
}): AnatomyVariant => ({
    id,
    label: id,
    selector: `.${id}`,
    restingBindings: [
        fill(bg),
        labelColor(fg),
        transparentEdge,
        xHover(xHoverBg),
    ],
    stateBindings: {
        hover: [fillHover(hoverBg)],
    },
});

const outlineVariant = ({
    id,
    fg,
    border,
    hoverBorder,
    xHoverBg,
}: {
    id: string;
    fg: `--${string}`;
    border: `--${string}`;
    hoverBorder: `--${string}`;
    xHoverBg: `--${string}`;
}): AnatomyVariant => ({
    id: `${id}-outline`,
    label: `${id} outline`,
    selector: `.outline.${id}`,
    restingBindings: [
        transparentFill,
        labelColor(fg),
        edge(border),
        xHover(xHoverBg),
    ],
    stateBindings: {
        hover: [edgeHover(hoverBorder)],
    },
});

const variants: readonly AnatomyVariant[] = [
    solidVariant({
        id: "accent",
        bg: "--bg-accent-subtle",
        fg: "--fg-accent",
        hoverBg: "--bg-accent-subtle-hover",
        xHoverBg: "--bg-accent-subtle-hover",
    }),
    outlineVariant({
        id: "accent",
        fg: "--fg-accent",
        border: "--border-accent",
        hoverBorder: "--border-accent-hover",
        xHoverBg: "--bg-accent-subtle",
    }),
    solidVariant({
        id: "primary",
        bg: "--bg-neutral",
        fg: "--fg-primary",
        hoverBg: "--bg-neutral-hover-on-fill",
        xHoverBg: "--bg-neutral-hover-on-fill",
    }),
    outlineVariant({
        id: "primary",
        fg: "--fg-primary",
        border: "--border-default",
        hoverBorder: "--border-hover",
        xHoverBg: "--bg-neutral",
    }),
    solidVariant({
        id: "neutral",
        bg: "--bg-neutral",
        fg: "--fg-secondary",
        hoverBg: "--bg-neutral-hover-on-fill",
        xHoverBg: "--bg-neutral-hover-on-fill",
    }),
    outlineVariant({
        id: "neutral",
        fg: "--fg-secondary",
        border: "--border-default",
        hoverBorder: "--border-hover",
        xHoverBg: "--bg-neutral",
    }),
    solidVariant({
        id: "error",
        bg: "--bg-error-subtle",
        fg: "--fg-error",
        hoverBg: "--bg-error-subtle-hover",
        xHoverBg: "--bg-error-subtle-hover",
    }),
    outlineVariant({
        id: "error",
        fg: "--fg-error",
        border: "--border-error",
        hoverBorder: "--border-error-hover",
        xHoverBg: "--bg-error-subtle",
    }),
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "border", property: "border-width", token: "--border-width" },
    {
        partId: "glyph",
        property: "color",
        literal: "currentColor",
        note: "Leading icons inherit the tag foreground.",
    },
];

export const tagAnatomy: ComponentAnatomySpec = {
    label: "Tag",
    schematic: "tag",
    rootSelector: ".tag",
    variantAxisLabel: "Color",
    parts,
    states,
    sharedBindings,
    variants,
    sizes: tagAnatomySizes,
};
